'use client';

import type { CSSProperties } from 'react';
import { Icon } from './icon';
import styles from './progress.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

/**
 * Floor for a determinate value. A job that has started but reports 0 must
 * still read as "something is happening" — a truly empty track looks broken.
 */
const VALUE_FLOOR = 8;

/**
 * The one place a job's stage position becomes a percentage. The bar and the
 * ring both read it, so they can never disagree about what "83%" means.
 */
export function jobProgressValue(stageIndex: number, stageCount: number): number {
  return Math.min(100, Math.max(VALUE_FLOOR, ((stageIndex + 1) / Math.max(1, stageCount)) * 100));
}

interface ProgressProps {
  /** 0–100. Omit for the indeterminate / queued treatment. */
  value?: number;
  tone?: 'accent' | 'sage';
  /** Accessible name. Omit only when an adjacent label already names it. */
  label?: string;
}

/**
 * ProgressBar — linear determinate progress with an indeterminate mode for
 * queued work. Width transitions on `--t-base` so a stage change *grows* the
 * bar instead of teleporting.
 */
export function ProgressBar({
  value,
  tone = 'accent',
  label,
  size = 'sm',
}: ProgressProps & { size?: 'sm' | 'lg' }) {
  const indeterminate = value == null;
  const clamped = indeterminate ? 0 : Math.min(100, Math.max(VALUE_FLOOR, value));

  return (
    <span
      className={cx(styles.track, styles[size], tone === 'sage' && styles.toneSage)}
      role="progressbar"
      aria-label={label}
      aria-valuenow={indeterminate ? undefined : Math.round(clamped)}
      aria-valuemin={indeterminate ? undefined : 0}
      aria-valuemax={indeterminate ? undefined : 100}
    >
      <span
        className={cx(styles.fill, indeterminate && styles.fillIndeterminate)}
        style={indeterminate ? undefined : ({ width: `${clamped}%` } as CSSProperties)}
      />
    </span>
  );
}

/**
 * ProgressRing — the same value model as `ProgressBar`, drawn as an arc for
 * the notification bell's trigger. Queued work holds the floor and pulses its
 * opacity; it deliberately does NOT spin, because spinning would claim work is
 * happening when nothing has started yet.
 */
export function ProgressRing({
  value,
  label,
  unread = false,
}: ProgressProps & { unread?: boolean }) {
  const queued = value == null;
  const clamped = queued ? VALUE_FLOOR : Math.min(100, Math.max(VALUE_FLOOR, value));

  // Geometry is token-driven; the maths needs the numbers, so read the two
  // structural tokens as literals that match --bell-ring-size/-stroke.
  const size = 22;
  const stroke = 2.25;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <span
      className={cx(styles.ring, queued && styles.ringQueued)}
      role="progressbar"
      aria-label={label}
      aria-valuenow={queued ? undefined : Math.round(clamped)}
      aria-valuemin={queued ? undefined : 0}
      aria-valuemax={queued ? undefined : 100}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={styles.ringSvg}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          fill="none"
          className={styles.ringTrack}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - clamped / 100)}
          strokeLinecap="round"
          className={styles.ringFill}
        />
      </svg>
      <Icon name="sparkles" size={12} strokeWidth={1.75} className={styles.ringGlyph} />
      {unread ? <span className={styles.ringDot} aria-hidden="true" /> : null}
    </span>
  );
}
